import React, {
  useEffect,
  useState,

  useMemo,
  useRef,
} from "react";
import { type NodeProps, type Node, useReactFlow } from "@xyflow/react";
import {
  X,
  Loader2,
  AlertTriangle,
  Upload,
  ChevronDown,
  Code,
  Plus,
  RefreshCw,
} from "lucide-react";
import oneDriveService, {
  OneDriveCredential,
  OneDriveFolder,
} from "../../services/oneDrive.service";
import syntaxHighlight from "../../utils/jsonViewer";
import { WorkflowBaseNode } from "../../common/shared/WorkflowBaseNode";
import { WorkflowNodeModal } from "../../common/shared/WorkflowNodeModal";

export const OneDriveUploadNode: React.FC<NodeProps<Node<any>>> = ({
  id,
  data,
  selected,
}) => {
  const { setNodes } = useReactFlow();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const [hasLoadedCredentials, setHasLoadedCredentials] = useState(false);
  const [hasLoadedFolders, setHasLoadedFolders] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const authPopupRef = useRef<Window | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [selectedCredentialId, setSelectedCredentialId] = useState<string>(
    data?.credential_id || "",
  );
  const [folderId, setFolderId] = useState<string>(data?.folder_id || "");
  const [filename, setFilename] = useState<string>(data?.filename || "");
  const [buffer, setBuffer] = useState<string>(data?.buffer || "");
  const [credentials, setCredentials] = useState<OneDriveCredential[]>([]);
  const [folders, setFolders] = useState<OneDriveFolder[]>([]);

  // ── Auth popup listener ───────────────────────────────────────────
  useEffect(() => {
    const listener = (event: MessageEvent) => {
      const allowed = [
        "http://localhost:3000",
        "http://localhost:5173",
        "https://nightly-dms.skill-mine.com",
        "https://demo-dms.skill-mine.com",
      ];
      if (!allowed.includes(event.origin)) return;
      if (
        event.data?.type === "ONEDRIVE_AUTH_SUCCESS" ||
        event.data?.status === "success"
      ) {
        setConnecting(false);
        loadCredentials();
        const popup = authPopupRef.current;
        if (popup && !popup.closed) popup.close();
      }
    };
    window.addEventListener("message", listener);
    return () => window.removeEventListener("message", listener);
  }, []);

  // ── Poll for popup close ──────────────────────────────────────────
  useEffect(() => {
    if (!connecting) return;
    const pollInterval = setInterval(() => {
      const popup = authPopupRef.current;
      if (popup && popup.closed) {
        authPopupRef.current = null;
        setConnecting(false);
        loadCredentials();
        clearInterval(pollInterval);
      }
    }, 1000);
    return () => clearInterval(pollInterval);
  }, [connecting]);

  // ── Click outside dropdown ────────────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as any)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const openAuthPopup = async () => {
    try {
      setConnecting(true);
      setErrorMsg(null);
      const templateId =
        data?.template_id || data?.request_id || localStorage.getItem("template_id");
      const authUrl = oneDriveService.getOneDriveLoginUrl(templateId);
      const width = 500;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;
      const popup = window.open(
        authUrl,
        "OneDriveAuthPopup",
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`,
      );
      if (!popup) throw new Error("Popup blocked! Please allow popups for this site.");
      authPopupRef.current = popup;
      popup.focus();
    } catch (err: any) {
      setErrorMsg(err?.message || "Failed to open login popup");
      setConnecting(false);
    }
  };

  const loadCredentials = async () => {
    const templateId = data?.template_id || localStorage.getItem("template_id");
    try {
      setErrorMsg(null);
      const response = await oneDriveService.listCredentials(templateId);
      if (response && response.success) {
        setCredentials(response.data || []);
      } else {
        setErrorMsg("Failed to load credentials");
      }
      setHasLoadedCredentials(true);
    } catch (err: any) {
      setErrorMsg(err?.message || "Failed to load credentials");
    }
  };

  const loadFolders = async () => {
    if (loadingFolders || !selectedCredentialId) return;
    try {
      setLoadingFolders(true);
      setErrorMsg(null);
      const templateId = data?.template_id || localStorage.getItem("template_id");
      const response = await oneDriveService.listFolders(selectedCredentialId, templateId);
      if (response && response.success && Array.isArray(response.data)) {
        setFolders(response.data);
      } else {
        setErrorMsg("Failed to load folders");
      }
      setHasLoadedFolders(true);
    } catch (err: any) {
      setErrorMsg(err?.message || "Failed to load folders");
    } finally {
      setLoadingFolders(false);
    }
  };

  // ── Load credentials + folders when modal opens ───────────────────
  useEffect(() => {
    if (!isModalOpen) return;
    if (!hasLoadedCredentials) loadCredentials();
    if (selectedCredentialId && !hasLoadedFolders) loadFolders();
  }, [isModalOpen, hasLoadedCredentials, hasLoadedFolders, selectedCredentialId]);

  // ── Reset folders on credential change ────────────────────────────
  useEffect(() => {
    if (selectedCredentialId) {
      setHasLoadedFolders(false);
    } else {
      setFolders([]);
      setFolderId("");
    }
  }, [selectedCredentialId]);

  const handleDrop = (e: React.DragEvent, fieldType: string) => {
    e.preventDefault();
    e.stopPropagation();
    const variable = e.dataTransfer.getData("text/plain");
    if (variable) {
      switch (fieldType) {
        case "folder_id": setFolderId(variable); break;
        case "filename": setFilename(variable); break;
        case "buffer": setBuffer(variable); break;
      }
    }
    setIsDraggingOver(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  // ── Sync config to ReactFlow ──────────────────────────────────────
  useEffect(() => {
    setNodes((nds) =>
      nds.map((n) =>
        n.id === id
          ? {
              ...n,
              data: {
                ...n.data,
                credential_id: selectedCredentialId,
                folder_id: folderId,
                filename,
                buffer,
              },
            }
          : n,
      ),
    );
  }, [selectedCredentialId, folderId, filename, buffer, id, setNodes]);

  // ── Validation ────────────────────────────────────────────────────
  const validationStatus = useMemo(() => {
    const errors: string[] = [];
    if (!selectedCredentialId) errors.push("OneDrive credential required");
    if (!filename) errors.push("Filename is required");
    if (!buffer) errors.push("Buffer is required");
    if (!folderId) errors.push("Folder selection is required");
    return { isValid: errors.length === 0, errors };
  }, [selectedCredentialId, filename, buffer, folderId]);

  const hasExecutionOutputs = data?.outputs && Object.keys(data.outputs).length > 0;

  const highlightedActualOutputs = useMemo(() => {
    if (hasExecutionOutputs) return syntaxHighlight(JSON.stringify(data.outputs, null, 2));
    return "";
  }, [data?.outputs, hasExecutionOutputs]);


  return (
    <WorkflowBaseNode
      id={id}
      data={data}
      selected={selected}
      title="OneDrive Upload"
      icon={<Upload className="w-4 h-4 text-sky-600" />}
      nodeDocsType="ONEDRIVE-UPLOAD"
      themeColor="sky"
      configureLabel="Configure Upload"
      isConfigured={validationStatus.isValid}
      validationErrors={validationStatus.errors}

      statusText={{
        running: "Executing...",
        success: "Executed Successfully",
        error: "Execution Failed",
      }}
      onConfigure={() => setIsModalOpen(true)}
    >

      {/* Modal */}
      <WorkflowNodeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="OneDrive Upload - Upload files"
        icon={<Upload className="w-5 h-5 text-sky-600" />}
        themeColor="sky"
        nodeDocsType="ONEDRIVE-UPLOAD"
        nodeId={id}
        validationStatus={validationStatus}
        saveDisabled={!validationStatus.isValid}
        saveLabel="Save Settings"
        nodeData={data}
        nodeIcon={<Upload className="w-6 h-6 text-gray-400" />}
        emptyOutputsText="No execution data"
        renderOutputsSidebar={() => (
          <div className="w-80 bg-gray-50 overflow-y-auto p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Code className="w-4 h-4 text-sky-600" />
                <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wide">
                  Output
                </h3>
              </div>
              {hasExecutionOutputs && (
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full text-green-700 bg-green-100">
                  {`${Object.keys(data.outputs).length} fields`}
                </span>
              )}
            </div>
            {hasExecutionOutputs ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 font-mono text-xs overflow-x-auto">
                <pre
                  className="whitespace-pre-wrap break-words"
                  dangerouslySetInnerHTML={{ __html: highlightedActualOutputs }}
                />
              </div>
            ) : (
              <div className="p-4 bg-white border-2 border-dashed border-gray-300 rounded-lg text-center">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Code className="w-5 h-5 text-gray-400" />
                </div>
                <p className="text-xs font-medium text-gray-600">No execution data</p>
                <p className="text-[10px] text-gray-400 mt-1">
                  Run the workflow to see outputs
                </p>
              </div>
            )}
          </div>
        )}
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          OneDrive Upload Configuration
        </h3>
        <p className="text-sm text-gray-500 mb-6">
          Connect to OneDrive and configure file upload settings
        </p>

        <div className="space-y-5">
          {/* Credential Dropdown */}
          <div className="p-4 bg-sky-50 border border-sky-200 rounded-lg">
            <h4 className="text-sm font-semibold text-gray-900 mb-2">Credentials</h4>
            <p className="text-xs text-gray-500 mb-3">
              Select which OneDrive account to use for uploading.
            </p>
            <div className="space-y-1">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Select Credential
              </label>
              <div className="relative" ref={dropdownRef}>
                <div
                  onClick={() => {
                    if (!isDropdownOpen) loadCredentials();
                    setIsDropdownOpen(!isDropdownOpen);
                  }}
                  className="w-full pl-3 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm bg-white hover:border-sky-400 focus:ring-2 focus:ring-sky-500 transition-all cursor-pointer flex items-center justify-between shadow-sm"
                >
                  <span className={`truncate ${selectedCredentialId ? "text-gray-900 font-medium" : "text-gray-400"}`}>
                    {selectedCredentialId
                      ? credentials.find((c) => c._id === selectedCredentialId)
                        ? `${credentials.find((c) => c._id === selectedCredentialId)?.user_info.user_name} (${credentials.find((c) => c._id === selectedCredentialId)?.user_info.user_email})`
                        : "Credential ID Selected"
                      : "Select a credential..."}
                  </span>
                  <ChevronDown
                    className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`}
                  />
                </div>
                {isDropdownOpen && (
                  <div className="absolute z-20 mt-1 w-full bg-white shadow-xl rounded-lg border border-gray-100 py-1 text-sm animate-in fade-in zoom-in-95 duration-100">
                    <div className="max-h-60 overflow-y-auto">
                      {credentials.length === 0 && (
                        <div className="px-3 py-2 text-gray-400 text-xs italic">
                          No credentials found
                        </div>
                      )}
                      {credentials.map((cred) => (
                        <div
                          key={cred._id}
                          onClick={() => {
                            setSelectedCredentialId(cred._id);
                            setIsDropdownOpen(false);
                          }}
                          className={`px-3 py-2 cursor-pointer flex items-center justify-between group transition-colors ${
                            selectedCredentialId === cred._id
                              ? "bg-sky-50 text-sky-700"
                              : "hover:bg-gray-50 text-gray-700"
                          }`}
                        >
                          <div className="truncate">
                            <div className="font-medium">{cred.user_info.user_name}</div>
                            <div className="text-xs text-gray-500 group-hover:text-gray-600">
                              {cred.user_info.user_email}
                            </div>
                          </div>
                          {selectedCredentialId === cred._id && (
                            <div className="w-1.5 h-1.5 bg-sky-600 rounded-full" />
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="border-t border-gray-100 mt-1 pt-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openAuthPopup();
                          setIsDropdownOpen(false);
                        }}
                        disabled={connecting}
                        className="w-full text-left px-3 py-2 text-sky-600 hover:bg-sky-50 transition-colors flex items-center gap-2 font-medium"
                      >
                        {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        {connecting ? "Connecting..." : "Create new credential"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex justify-end mt-2">
                <button
                  onClick={loadCredentials}
                  className="text-xs text-gray-500 hover:text-sky-600 flex items-center gap-1 transition-colors"
                >
                  <RefreshCw className={`w-3 h-3 ${connecting ? "animate-spin" : ""}`} />
                  Refresh List
                </button>
              </div>
            </div>
            {errorMsg && (
              <p className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded border border-red-100 flex items-center gap-2">
                <AlertTriangle className="w-3 h-3" />
                {errorMsg}
              </p>
            )}
            {!selectedCredentialId && (
              <p className="text-xs text-red-500 mt-1">OneDrive credential is required</p>
            )}
          </div>

          {/* Folder Selection */}
          {selectedCredentialId && (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Folder Selection</h4>
              <p className="text-xs text-gray-500 mb-3">Select the folder to upload files to.</p>
              <div className="space-y-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  OneDrive Folder <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <select
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    value={folderId}
                    onChange={(e) => setFolderId(e.target.value)}
                  >
                    <option value="">Select a folder...</option>
                    {folders.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.name} {f.childCount > 0 ? `(${f.childCount} items)` : "(empty)"}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={loadFolders}
                    disabled={loadingFolders}
                    className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                    title="Refresh Folders"
                  >
                    <RefreshCw className={`w-4 h-4 ${loadingFolders ? "animate-spin" : ""}`} />
                  </button>
                </div>
                {!folderId && (
                  <p className="text-xs text-red-500 mt-1">Folder selection is required</p>
                )}
              </div>
            </div>
          )}

          {/* Filename */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filename <span className="text-red-500">*</span>
            </label>
            <div
              onDrop={(e) => handleDrop(e, "filename")}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`min-h-[80px] px-4 py-3 border-2 border-dashed rounded-lg text-sm transition-all ${
                isDraggingOver
                  ? "border-sky-400 bg-sky-50"
                  : filename
                    ? "border-sky-300 bg-sky-50/50"
                    : "border-gray-300 bg-gray-50 hover:border-gray-400"
              }`}
            >
              {filename ? (
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-gray-500 mb-1">Selected field</div>
                    <code className="text-xs text-sky-700 font-mono break-all">{filename}</code>
                  </div>
                  <button
                    onClick={() => setFilename("")}
                    className="text-gray-400 hover:text-gray-600 p-1 hover:bg-white rounded transition-colors shrink-0"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center text-gray-400">
                  <span className="text-sm">Drag and drop filename here</span>
                  <span className="text-xs mt-1">
                    From previous node outputs (e.g., PDFGeneratorNode.outputs.filename)
                  </span>
                </div>
              )}
            </div>
            {!filename && (
              <p className="text-xs text-red-500 mt-1">Filename is required</p>
            )}
          </div>

          {/* File Buffer */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              File Buffer <span className="text-red-500">*</span>
            </label>
            <div
              onDrop={(e) => handleDrop(e, "buffer")}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`min-h-[80px] px-4 py-3 border-2 border-dashed rounded-lg text-sm transition-all ${
                isDraggingOver
                  ? "border-sky-400 bg-sky-50"
                  : buffer
                    ? "border-sky-300 bg-sky-50/50"
                    : "border-gray-300 bg-gray-50 hover:border-gray-400"
              }`}
            >
              {buffer ? (
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-gray-500 mb-1">Selected field</div>
                    <code className="text-xs text-sky-700 font-mono break-all">{buffer}</code>
                  </div>
                  <button
                    onClick={() => setBuffer("")}
                    className="text-gray-400 hover:text-gray-600 p-1 hover:bg-white rounded transition-colors shrink-0"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center text-gray-400">
                  <span className="text-sm">Drag and drop buffer here</span>
                  <span className="text-xs mt-1">
                    From previous node outputs (e.g., PDFGeneratorNode.outputs.buffer)
                  </span>
                </div>
              )}
            </div>
            {!buffer && (
              <p className="text-xs text-red-500 mt-1">File buffer is required</p>
            )}
          </div>
        </div>
      </WorkflowNodeModal>
    </WorkflowBaseNode>
  );
};

export default OneDriveUploadNode;
